export interface Week {
  id: string;
  weekNumber: number;
  title: string;
  sessions: Chapter[];
}

export interface Module {
  id: string;
  title: string;
  description: string;
  weeks: Week[];
  chapters: Chapter[];
  progress: number;
  estimatedHours: number;
}

export interface Chapter {
  id: string;
  moduleId: string;
  title: string;
  description: string;
  content: string;
  videoUrl?: string;
  exercises?: Exercise[];
  completed: boolean;
  estimatedMinutes: number;
}

export interface Exercise {
  id: string;
  title: string;
  description: string;
  type: 'text' | 'multiple-choice' | 'reflection';
  questions: Question[];
}

export interface Question {
  id: string;
  question: string;
  type: 'text' | 'multiple-choice';
  options?: string[];
  correctAnswer?: string;
}

const module1Sessions = [
  { title: 'Välkommen till Gruppen - Vi Börjar Tillsammans', desc: 'Gruppregler, förtroende och första stegen mot förändring', content: 'Gruppövning: Presentationsrunda, förväntningar och oro. Introducera KBT på ett enkelt sätt' },
  { title: 'Vad Påverkar Mitt Liv? - Min Bakgrund och Situation', desc: 'Gruppsamtal om hur miljö, ekonomi och livssituation påverkar våra val', content: 'Gruppövning: Kartläggning av påverkansfaktorer, dela erfarenheter i trygga par' },
  { title: 'Mina Risker och Styrkor - Central Eight i Praktiken', desc: 'Identifiera personliga riskområden och bygga medvetenhet om egna styrkor', content: 'Gruppövning: Personlig riskprofil med gruppstöd, styrkebaserat fokus' },
  { title: 'Vad Är Viktigt för Mig? - Mina Värderingar och Drömmar', desc: 'Upptäck dina kärnvärden och skapa realistiska, meningsfulla mål', content: 'Gruppövning: Värderingsexploration, målsättning med gruppens stöd och feedback' },
  { title: 'Övning Gör Mästare - Tillämpa Det Vi Lärt', desc: 'Praktiska övningar för att befästa första modulens lärdomar', content: 'Gruppövning: Rollspel, problemlösning och förberedelse för nästa modul' },
  { title: 'Reflektion och Planering - Vad Har Vi Lärt Oss?', desc: 'Gruppreflektion över första modulens insikter och planering', content: 'Gruppövning: Återkoppling, fira framsteg och sätta upp personliga mål' },
  { title: 'Fördjupning - Min Personliga Berättelse', desc: 'Utforska din livsberättelse och identifiera mönster', content: 'Gruppövning: Livsberättelse i par, identifiera vändpunkter och lärdomar' },
  { title: 'Socialt Stöd - Vem Finns Där för Mig?', desc: 'Kartlägg ditt sociala nätverk och identifiera stödjande personer', content: 'Gruppövning: Nätverkskarta, diskussion om gränser och stödjande relationer' },
  { title: 'Stress och Trauma - Förstå Mina Reaktioner', desc: 'Lär dig om hur stress och trauma påverkar kropp och sinne', content: 'Gruppövning: Stressreaktioner, grundläggande copingstrategier och trygghet' },
  { title: 'Motivation för Förändring - Varför Vill Jag Detta?', desc: 'Utforska din motivation för förändring och hantera ambivalens', content: 'Gruppövning: Motivationsbalans, för- och nackdelar med förändring' },
  { title: 'Praktik och Integration - Använda Verktygen', desc: 'Praktiska övningar för att integrera modulens verktyg i vardagen', content: 'Gruppövning: Scenarioträning, problemlösning och action planning' },
  { title: 'Avslutning Modul 1 - Redo för Nästa Steg', desc: 'Summera lärdomar från första månaden och förbered dig för tankemönster', content: 'Gruppövning: Återblick, fira framsteg och sätta mål för modul 2' }
];

const module2Sessions = [
  { title: 'Att Känna Igen Mina Tankar - Tankejägaren', desc: 'Lär dig observera och identifiera problematiska tankemönster', content: 'Gruppövning: Tankejägartekniken, dela exempel från vardagen i gruppen' },
  { title: 'Utmana Mina Rättfärdiganden - Tankedomstolen', desc: 'Träna på att ifrågasätta tankemönster som rättfärdigar skadliga beteenden', content: 'Gruppövning: Tankedomstolen, rollspel och gruppstöd för att utmana tankar' },
  { title: 'STOPP! - Bryta Impulsiva Mönster', desc: 'Lär dig STOPP-tekniken för att skapa paus mellan impuls och handling', content: 'Gruppövning: STOPP-teknik träning, scenarioträning med gruppstöd' },
  { title: 'Lösa Problem Steg för Steg - Femstegsmodellen', desc: 'Lär dig strukturerad metod för att hantera vardagsproblem', content: 'Gruppövning: Problemlösningsträning med verkliga exempel från gruppmedlemmar' },
  { title: 'Förstå Hur Andra Påverkas - Ripple Effect', desc: 'Utveckla empati genom att förstå hur dina handlingar påverkar andra', content: 'Gruppövning: Ripple Effect-analys, perspektivtagande och empatiträning' },
  { title: 'Fördjupning - Mina Tankefällor', desc: 'Identifiera personliga tankefällor och utveckla alternativa strategier', content: 'Gruppövning: Personlig kartläggning av tankefällor med gruppfeedback' },
  { title: 'Hantera Ilska och Frustration', desc: 'Lär dig känna igen och hantera starka känslor konstruktivt', content: 'Gruppövning: Ilskehantering, avslappningstekniker och gruppstöd' },
  { title: 'Självkänsla och Självsabotage', desc: 'Utforska hur låg självkänsla kan leda till sabotage', content: 'Gruppövning: Självkänsloarbete, positiv självbild och gruppvalidering' },
  { title: 'Peer Pressure och Gruppens Påverkan', desc: 'Lär dig hantera påtryckningar och fatta självständiga beslut', content: 'Gruppövning: Rollspel om grupptryck, träna att säga nej och stå för sina val' },
  { title: 'Mindfulness - Vara Närvarande i Nuet', desc: 'Grundläggande mindfulness-tekniker för medvetenhet och impulskontroll', content: 'Gruppövning: Guidade mindfulness-övningar och reflektion' },
  { title: 'Praktik och Integration - Verktyg i Vardagen', desc: 'Praktiska övningar för att integrera tanketekniker i vardagen', content: 'Gruppövning: Vardagsscenarier, problemlösning och action planning' },
  { title: 'Avslutning Modul 2 - Mina Nya Tankeverktyg', desc: 'Summera tankeverktygen och förbered dig för livsstilsförändringar', content: 'Gruppövning: Återblick, fira framsteg och planera för modul 3' }
];

const module3Sessions = [
  { title: 'Hantera Stress på Rätt Sätt - Mina Copingstrategier', desc: 'Kartlägg stresshanteringsmetoder och lär dig hälsosamma alternativ', content: 'Gruppövning: Coping-inventering, dela strategier och utveckla nya verktyg' },
  { title: 'Kommunicera på Ett Bättre Sätt - Jag-budskap och Assertivitet', desc: 'Lär dig kommunicera behov och gränser respektfullt och tydligt', content: 'Gruppövning: Assertivitetsträning, rollspel och kommunikationstekniker' },
  { title: 'Bygga En Ny Vardag - Rutiner och Struktur', desc: 'Skapa strukturerad och meningsfull vardag genom beteendeaktivering', content: 'Gruppövning: Vardagsplanering, aktivitetsschema och målsättning' },
  { title: 'Förebygga Återfall - Min Personliga Plan', desc: 'Skapa personlig kris- och preventionsplan baserat på triggers', content: 'Gruppövning: Återfallspreventionsplan, identifiera triggers och utveckla strategier' },
  { title: 'Fördjupning - Hantera Svåra Känslor', desc: 'Lär dig känna igen, acceptera och hantera svåra känslor konstruktivt', content: 'Gruppövning: Känsloreglering, toleransträning och alternativa strategier' },
  { title: 'Relationer och Gränser', desc: 'Lär dig sätta hälsosamma gränser och bygga stödjande relationer', content: 'Gruppövning: Gränsträning, relationskartläggning och konflikthantering' },
  { title: 'Självvård och Välmående', desc: 'Utveckla rutiner för fysisk och mental hälsa som stödjer återhämtning', content: 'Gruppövning: Självvårdsplan, hälsosamma aktiviteter och gruppstöd' },
  { title: 'Fritid och Meningsfulla Aktiviteter', desc: 'Utforska nya intressen som kan ersätta problematiska beteenden', content: 'Gruppövning: Intresseinventering, aktivitetsplanering och peer support' },
  { title: 'Ekonomi och Praktisk Planering', desc: 'Grundläggande ekonomisk planering och praktiska verktyg för vardagen', content: 'Gruppövning: Budgetträning, ekonomisk planering och resursdelning' },
  { title: 'Motivation och Förändringens Utmaningar', desc: 'Hantera svackor i motivationen och utveckla långsiktigt engagemang', content: 'Gruppövning: Motivationsarbete, hantera motstånd och utveckla uthållighet' },
  { title: 'Praktik och Integration - Leva Mitt Nya Liv', desc: 'Praktiska övningar för att integrera livsstilsförändringar i vardagen', content: 'Gruppövning: Livsstilsplanering, scenarioträning och handlingsplaner' },
  { title: 'Avslutning Modul 3 - Mitt Förändrade Liv', desc: 'Fira livsstilsförändringar och förbered dig för återintegration', content: 'Gruppövning: Återblick, fira framsteg och sätta mål för sista modulen' }
];

const module4Sessions = [
  { title: 'Praktiska Livskunskaper - Hantera Vardagen', desc: 'Workshop i budgetering, digitala verktyg och kontakt med myndigheter', content: 'Gruppövning: Praktisk träning, CV-skrivning och digitala färdigheter' },
  { title: 'Arbete och Framtidsdrömmar - Min Karriärplan', desc: 'Skapa realistisk plan för arbete eller utbildning och hantera förflutet', content: 'Gruppövning: Karriärplanering, jobbsökningsstrategier och förtroendebyggande' },
  { title: 'Bygga Mitt Stödnätverk - Nya Vänskap och Relationer', desc: 'Kartlägg och förstärk socialt nätverk med människor som stödjer förändring', content: 'Gruppövning: Nätverksbyggande, vänskap och prosociala kontakter' },
  { title: 'Fördjupning - Hantera Stigma och Fördomar', desc: 'Lär dig hantera andras fördomar och bygga upp självförtroende i samhället', content: 'Gruppövning: Stigmahantering, självförsvar och identitetsarbete' },
  { title: 'Legala Frågor och Rättigheter', desc: 'Förstå dina rättigheter och skyldigheter samt hantera legal stress', content: 'Gruppövning: Juridisk information, rättighetskännedom och stresshantering' },
  { title: 'Boende och Stabilitet', desc: 'Planera för stabilt boende och skapa en trygg hemmiljö', content: 'Gruppövning: Boendeplanering, trygghet och hemkänsla' },
  { title: 'Familj och Nära Relationer', desc: 'Återuppbygga förtroende och förbättra relationer med familj och vänner', content: 'Gruppövning: Familjerelationer, förlåtelse och förtroendebyggande' },
  { title: 'Mentorskap och Ge Tillbaka', desc: 'Utforska möjligheter att hjälpa andra och bidra positivt till samhället', content: 'Gruppövning: Mentorskap, volontärarbete och community engagement' },
  { title: 'Långsiktig Planering - Mitt Liv om 1-5 År', desc: 'Skapa detaljerad plan för framtiden med realistiska mål och milstolpar', content: 'Gruppövning: Framtidsplanering, målsättning och visionsarbete' },
  { title: 'Krisplanering - När Livet Blir Svårt', desc: 'Utveckla strategier för att hantera framtida kriser utan återfall', content: 'Gruppövning: Krisplan, nödkontakter och säkerhetsstrategier' },
  { title: 'Fira Framsteg - Min Resa Genom Programmet', desc: 'Reflektion över hela programmet och firande av alla framsteg', content: 'Gruppövning: Programreflektion, firande och erkännande av framsteg' },
  { title: 'Avslutning och Framtiden - 30-60-90 Dagars Plan', desc: 'Skapa konkret handlingsplan för första månaderna efter programmet', content: 'Gruppövning: Handlingsplan, uppsägelse och fortsatt stöd' }
];

const createChapters = (moduleId: string, startSession: number, sessions: { title: string; desc: string; content: string }[]) => {
  return sessions.map((session, index) => ({
    id: `kapitel-${moduleId.split('-')[1]}-${index + 1}`,
    moduleId,
    title: `Session ${startSession + index}: ${session.title}`,
    description: session.desc,
    content: session.content,
    completed: false,
    estimatedMinutes: 90
  }));
};

const createWeeks = (moduleId: string, startSession: number, sessions: { title: string; desc: string; content: string }[]) => {
  const chapters = createChapters(moduleId, startSession, sessions);
  return [
    {
      id: `${moduleId}-week-1`,
      weekNumber: 1,
      title: 'Vecka 1: Grunden: Allians, Motivation och Programkontrakt',
      sessions: chapters.slice(0, 3)
    },
    {
      id: `${moduleId}-week-2`,
      weekNumber: 2,
      title: 'Vecka 2: Socioekonomisk Kontext och Livshistoria',
      sessions: chapters.slice(3, 6)
    },
    {
      id: `${moduleId}-week-3`,
      weekNumber: 3,
      title: 'Vecka 3: Personlig Risk- och Skyddsprofil',
      sessions: chapters.slice(6, 9)
    },
    {
      id: `${moduleId}-week-4`,
      weekNumber: 4,
      title: 'Vecka 4: Värderingar som Kompass och Livsmål',
      sessions: chapters.slice(9, 12)
    }
  ];
};

export const MODULES: Module[] = [
  {
    id: 'modul-1',
    title: 'Modul 1: Helhetsorienterande Förståelse',
    description: 'Gruppterapi för att bygga förtroende, förstå hur tankar och beteenden hänger ihop, och identifiera personliga styrkor',
    progress: 0,
    estimatedHours: 18,
    weeks: createWeeks('modul-1', 1, module1Sessions),
    chapters: createChapters('modul-1', 1, module1Sessions)
  },
  {
    id: 'modul-2',
    title: 'Modul 2: Justeringar av Kognitiva Mönster',
    description: 'Gruppterapi för att upptäcka och förändra problematiska tankemönster, öka impulskontroll och lära praktisk problemlösning',
    progress: 0,
    estimatedHours: 18,
    weeks: createWeeks('modul-2', 13, module2Sessions),
    chapters: createChapters('modul-2', 13, module2Sessions)
  },
  {
    id: 'modul-3',
    title: 'Modul 3: Ändring av Livsstrategier',
    description: 'Gruppterapi för att utveckla hälsosamma copingstrategier, förbättra kommunikation och bygga en strukturerad vardag',
    progress: 0,
    estimatedHours: 18,
    weeks: createWeeks('modul-3', 25, module3Sessions),
    chapters: createChapters('modul-3', 25, module3Sessions)
  },
  {
    id: 'modul-4',
    title: 'Modul 4: Praktiskt Stöd för Återintegration',
    description: 'Gruppterapi för praktisk återintegration, arbete/utbildning, och byggande av långsiktigt stöd',
    progress: 0,
    estimatedHours: 18,
    weeks: createWeeks('modul-4', 37, module4Sessions),
    chapters: createChapters('modul-4', 37, module4Sessions)
  }
];